import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../patients/utils/patientUtils';

export interface Medication {
  id: string;
  patient_id: string;
  consultation_id?: string | null;
  name: string;
  dosage?: string;
  frequency?: string;
  route?: string;
  start_date?: string | null;
  end_date?: string | null;
  status: string;
  prescribed_by?: string | null;
  prescribed_by_name?: string | null;
  notes?: string;
}

interface FormDataState {
  name: string;
  dosage: string;
  frequency: string;
  route: string;
  start_date: string;
  end_date: string;
  status: string;
  notes: string;
}

const todayIso = () => new Date().toISOString().split('T')[0];

const emptyFormData = (): FormDataState => ({
  name: '',
  dosage: '',
  frequency: '',
  route: '',
  start_date: todayIso(),
  end_date: '',
  status: 'Active',
  notes: ''
});

interface AddMedicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  patient?: { first_name: string; last_name: string; date_of_birth?: string } | null;
  onMedicationAdded: (medication: Medication) => void;
  // Set when opened from an active consult - see AddDiagnosisModal for the
  // same pattern. Omit to add directly to a patient's file, no consult
  // required.
  consultationId?: string | null;
  // When set, edits this medication instead of creating a new one -
  // used for the "discontinue" action (set status + end_date on an
  // existing Active medication).
  editingMedication?: Medication | null;
}

const AddMedicationModal: React.FC<AddMedicationModalProps> = ({
  isOpen,
  onClose,
  patientId,
  patient,
  onMedicationAdded,
  consultationId,
  editingMedication,
}) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState<FormDataState>(emptyFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    if (editingMedication) {
      setFormData({
        name: editingMedication.name,
        dosage: editingMedication.dosage || '',
        frequency: editingMedication.frequency || '',
        route: editingMedication.route || '',
        start_date: editingMedication.start_date || todayIso(),
        end_date: editingMedication.end_date || todayIso(),
        status: 'Discontinued',
        notes: editingMedication.notes || '',
      });
    } else {
      setFormData(emptyFormData());
    }
  }, [isOpen, editingMedication]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Please enter a medication name');
      return;
    }
    if (formData.status === 'Discontinued' && !formData.end_date) {
      setError('Please set the date this medication was discontinued');
      return;
    }
    if (!token) return;

    setIsSubmitting(true);
    setError('');

    try {
      const payload: Record<string, unknown> = {
        patient_id: patientId,
        name: formData.name.trim(),
        dosage: formData.dosage,
        frequency: formData.frequency,
        route: formData.route,
        start_date: formData.start_date || null,
        end_date: formData.status === 'Discontinued' ? (formData.end_date || null) : null,
        status: formData.status,
        notes: formData.notes,
        consultation_id: consultationId || null,
      };

      const response = editingMedication
        ? await apiCall(`/clinical/patients/medications/${editingMedication.id}/`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }, token)
        : await apiCall('/clinical/patients/medications/create/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }, token);

      if (response.ok) {
        const saved = await response.json();
        onMedicationAdded(saved);
        handleClose();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || errorData.message || 'Failed to save medication');
      }
    } catch (error) {
      console.error('Error saving medication:', error);
      setError('Failed to save medication');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(emptyFormData());
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-3xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-base-300">
          <h2 className="text-xl font-semibold text-base-content">
            {editingMedication ? 'Discontinue Medication' : 'Add Medication'} for {patient ? `${patient.first_name} ${patient.last_name}` : 'Patient'}
            {patient?.date_of_birth && ` (DOB: ${formatDate(patient.date_of_birth)})`}
          </h2>
          <button
            onClick={handleClose}
            className="text-base-content opacity-60 hover:opacity-100 transition-colors"
          >
            <FaTimes className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          {error && (
            <div className="alert alert-error">
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-base-content mb-2">
              Medication Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input input-bordered w-full"
              placeholder="e.g., Pregabalin (Lyrica)"
              required
              disabled={!!editingMedication}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-base-content mb-2">
                Dosage
              </label>
              <input
                type="text"
                value={formData.dosage}
                onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                className="input input-bordered w-full"
                placeholder="e.g., 75mg"
                disabled={!!editingMedication}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-base-content mb-2">
                Frequency
              </label>
              <input
                type="text"
                value={formData.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                className="input input-bordered w-full"
                placeholder="e.g., BID"
                disabled={!!editingMedication}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-base-content mb-2">
                Route
              </label>
              <select
                value={formData.route}
                onChange={(e) => setFormData({ ...formData, route: e.target.value })}
                className="select select-bordered w-full"
                disabled={!!editingMedication}
              >
                <option value="">Not specified</option>
                <option value="Oral">Oral</option>
                <option value="Topical">Topical</option>
                <option value="Injection">Injection</option>
                <option value="Inhaled">Inhaled</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-base-content mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="input input-bordered w-full"
                disabled={!!editingMedication}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-base-content mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="select select-bordered w-full"
              >
                <option value="Active">Active</option>
                <option value="Discontinued">Discontinued</option>
              </select>
            </div>
          </div>

          {formData.status === 'Discontinued' && (
            <div>
              <label className="block text-sm font-medium text-base-content mb-2">
                Discontinued Date *
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="input input-bordered w-full max-w-xs"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-base-content mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="textarea textarea-bordered w-full"
              placeholder="Additional notes..."
            />
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-ghost flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.name.trim()}
              className="btn btn-primary flex-1"
            >
              {isSubmitting ? 'Saving...' : editingMedication ? 'Discontinue' : 'Add Medication'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMedicationModal;
